import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Users, 
  Award, 
  Target,
  Activity,
  Code,
  Calendar,
  RefreshCw,
  CheckCircle,
  Mail
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,

} from 'recharts';
import DatePicker from 'react-datepicker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import "react-datepicker/dist/react-datepicker.css";

const Teams = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamsData, setTeamsData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(subDays(new Date(), 30)),
    endDate: endOfDay(new Date())
  });
  const [sortBy, setSortBy] = useState('requestsPerUser');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [includeIT, setIncludeIT] = useState(true);

  const loadTeamsDataRef = useRef();

  const loadTeamsData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Validate that both dates are selected
      if (!dateRange.startDate || !dateRange.endDate) {
        toast.error('Please select a valid date range');
        setLoading(false);
        return;
      }
      
      const params = {
        startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
        endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
        includeIT: includeIT
      };

      const response = await api.get(`${endpoints.analytics.teams}?${new URLSearchParams(params)}`);
      console.log('Teams API response:', response.data);
      setTeamsData(response.data);
    } catch (error) {
      console.error('Failed to load teams data:', error);
      toast.error('Failed to load teams analytics');
      
      // Set empty data structure
      setTeamsData({
        dateRange: { 
          startDate: dateRange.startDate ? format(dateRange.startDate, 'yyyy-MM-dd') : null, 
          endDate: dateRange.endDate ? format(dateRange.endDate, 'yyyy-MM-dd') : null
        },
        teams: []
      });
    } finally {
      setLoading(false);
    }
  }, [dateRange, includeIT]);

  // Store the latest function reference
  loadTeamsDataRef.current = loadTeamsData;

  const handleDateRangeChange = (field, date) => {
    setDateRange(prev => ({
      ...prev,
      [field]: date
    }));
  };

  // Only load data when both dates are selected
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      loadTeamsDataRef.current();
    }
  }, [dateRange, includeIT]);

  const sendTeamsPageEmail = async () => {
    try {
      setSendingEmail(true);
      
      // Calculate summary for the email
      const emailSummary = calculateExecutiveSummary();
      const emailSortedTeams = getSortedTeams();
      
      // Create a cleaner version of the HTML content
      const cleanHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Teams Page Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
            .summary-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; margin-bottom: 30px; }
            .card { background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
            .card h3 { margin: 0 0 10px 0; color: #333; }
            .card p { margin: 0; font-size: 24px; font-weight: bold; color: #2563eb; }
            .team-table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            .team-table th, .team-table td { padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
            .team-table th { background: #f9fafb; font-weight: 600; }
            .date-range { background: #e0f2fe; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Team Performance Analytics Report</h1>
            <div class="date-range">
              <strong>Date Range:</strong> ${format(dateRange.startDate, 'MMM d, yyyy')} to ${format(dateRange.endDate, 'MMM d, yyyy')}
            </div>
          </div>
          
          <div class="summary-cards">
            <div class="card">
              <h3>Total Teams</h3>
              <p>${emailSummary.totalTeams}</p>
            </div>
            <div class="card">
              <h3>Active Developers</h3>
              <p>${emailSummary.totalActiveUsers}/${emailSummary.totalUsers}</p>
            </div>
            <div class="card">
              <h3>Requests/User</h3>
              <p>${emailSummary.avgRequests}</p>
            </div>
            <div class="card">
              <h3>Adoption Rate</h3>
              <p>${emailSummary.overallAdoptionRate}%</p>
            </div>
          </div>
          
          ${emailSummary.topPerformingTeam ? `
          <div class="card">
            <h2>Top Performing Team (Per-User)</h2>
            <p><strong>Team:</strong> ${emailSummary.topPerformingTeam.teamName}</p>
            <p><strong>Requests/User:</strong> ${emailSummary.topPerformingTeam.totalUsers > 0 ? Math.round((emailSummary.topPerformingTeam.totalRequests || 0) / emailSummary.topPerformingTeam.totalUsers) : 0}</p>
          </div>
          ` : ''}
          
          <div class="card">
            <h2>Team Performance Details</h2>
            <table class="team-table">
              <thead>
                <tr>
                  <th>Team Name</th>
                  <th>Users</th>
                  <th>Active Users</th>
                  <th>Total Requests</th>
                  <th>Requests/User</th>
                  <th>Acceptance Rate</th>
                  <th>Engagement Rate</th>
                </tr>
              </thead>
              <tbody>
                ${emailSortedTeams.map(team => `
                  <tr>
                    <td>${team.teamName || 'Unknown'}</td>
                    <td>${team.totalUsers || 0}</td>
                    <td>${team.activeUsers || 0}</td>
                    <td>${team.totalRequests || 0}</td>
                    <td>${team.totalUsers > 0 ? Math.round((team.totalRequests || 0) / team.totalUsers) : 0}</td>
                    <td>${Math.min(team.acceptanceRate || 0, 100)}%</td>
                    <td>${Math.min(team.engagementRate || 0, 100)}%</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <div style="margin-top: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
            <p><em>Report generated on ${new Date().toLocaleString()} by ${user?.email || 'Admin'}</em></p>
          </div>
        </body>
        </html>
      `;

      // Send the email
      const response = await api.post(endpoints.admin.sendgrid.teamsPage, {
        htmlContent: cleanHtml,
        dateRange: {
          startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
          endDate: format(dateRange.endDate, 'yyyy-MM-dd')
        }
      });

      if (response.data.success) {
        toast.success('Teams page email sent successfully!');
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send teams page email:', error);
      toast.error(error.response?.data?.message || 'Failed to send teams page email');
    } finally {
      setSendingEmail(false);
    }
  };

  const calculateExecutiveSummary = () => {
    if (!teamsData?.teams?.length) {
      return {
        totalTeams: 0,
        totalUsers: 0,
        totalActiveUsers: 0,
        avgProductivity: 0,
        topPerformingTeam: null,
        overallAdoptionRate: 0
      };
    }

    const teams = teamsData.teams;
    const totalTeams = teams.length;
    const totalUsers = teams.reduce((sum, team) => sum + (team.totalUsers || 0), 0);
    const totalActiveUsers = teams.reduce((sum, team) => sum + (team.activeUsers || 0), 0);
    // We no longer need totalLinesAdded as we're focusing on requests
    // Calculate total requests across all teams
    const totalRequests = teams.reduce((sum, team) => sum + (team.totalRequests || 0), 0);
    const avgRequests = totalActiveUsers > 0 ? Math.round(totalRequests / totalActiveUsers) : 0;
    
    // Calculate top performing team based on per-user metrics (requests per user)
    const topPerformingTeam = teams.reduce((top, team) => {
      const currentRequestsPerUser = team.totalUsers > 0 ? (team.totalRequests || 0) / team.totalUsers : 0;
      const topRequestsPerUser = top?.totalUsers > 0 ? (top.totalRequests || 0) / top.totalUsers : 0;
      
      return currentRequestsPerUser > topRequestsPerUser ? team : top;
    }, null);

    const overallAdoptionRate = totalUsers > 0 ? Math.round((totalActiveUsers / totalUsers) * 100) : 0;

    return {
      totalTeams,
      totalUsers,
      totalActiveUsers,
      avgRequests,
      topPerformingTeam,
      overallAdoptionRate
    };
  };

  const getSortedTeams = () => {
    if (!teamsData?.teams?.length) return [];

    return [...teamsData.teams].sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'requestsPerUser') {
        aValue = a.totalUsers > 0 ? (a.totalRequests || 0) / a.totalUsers : 0;
        bValue = b.totalUsers > 0 ? (b.totalRequests || 0) / b.totalUsers : 0;
      } else if (sortBy === 'linesPerUser') {
        aValue = a.totalUsers > 0 ? (a.totalLinesAdded || 0) / a.totalUsers : 0;
        bValue = b.totalUsers > 0 ? (b.totalLinesAdded || 0) / b.totalUsers : 0;
      } else {
        aValue = a[sortBy] || 0;
        bValue = b[sortBy] || 0;
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });
  };

  const getFeatureAdoptionData = () => {
    if (!teamsData?.teams?.length) return [];

    return teamsData.teams
      .filter(team => team.totalUsers > 0) // Only include teams with users
      .map(team => {
        // Calculate per-user metrics
        const usersCount = team.totalUsers || 1; // Prevent division by zero
        return {
          name: team.teamName || 'Unknown',
          composerRequests: Math.round((team.totalComposerRequests || 0) / usersCount),
          chatRequests: Math.round((team.totalChatRequests || 0) / usersCount),
          agentRequests: Math.round((team.totalAgentRequests || 0) / usersCount),
          cmdKUsages: Math.round((team.totalCmdKUsages || 0) / usersCount),
          totalFeatureUsage: Math.round(
            ((team.totalComposerRequests || 0) + 
             (team.totalChatRequests || 0) + 
             (team.totalAgentRequests || 0) + 
             (team.totalCmdKUsages || 0)) / usersCount
          ),
          usersCount // Include user count for tooltip
        };
      })
      .filter(team => team.totalFeatureUsage > 0) // Only show teams with feature usage
      .sort((a, b) => b.totalFeatureUsage - a.totalFeatureUsage); // Sort by total usage per user
  };

  const getProductivityTrendData = () => {
    if (!teamsData?.teams?.length) return [];

    return teamsData.teams.map(team => {
      // Calculate requests per user instead of lines per user
      const requestsPerUser = team.totalUsers > 0 ? Math.round((team.totalRequests || 0) / team.totalUsers) : 0;
      return {
        name: team.teamName || 'Unknown',
        requestsPerUser: requestsPerUser,
        acceptanceRate: Math.min(team.acceptanceRate || 0, 100), // Cap at 100%
        engagementRate: Math.min(team.engagementRate || 0, 100) // Cap at 100%
      };
    })
    .filter(team => team.requestsPerUser > 0)
    .sort((a, b) => b.requestsPerUser - a.requestsPerUser); // Sort by requests per user in descending order
  };

  const summary = calculateExecutiveSummary();
  const sortedTeams = getSortedTeams();
  const featureAdoptionData = getFeatureAdoptionData();
  const productivityTrendData = getProductivityTrendData();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Performance Analytics</h1>
          <p className="text-gray-600 mt-1">Executive insights into team productivity and AI adoption</p>
        </div>
        
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeIT}
              onChange={(e) => setIncludeIT(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Include IT
          </label>
          <button
            onClick={() => loadTeamsDataRef.current()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          
          {/* Email Me button - Admin only */}
          {user?.role === 'admin' && (
            <button
              onClick={sendTeamsPageEmail}
              disabled={sendingEmail}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail size={16} />
              {sendingEmail ? 'Sending...' : 'Email Me'}
            </button>
          )}
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
          </div>
          <div className="flex items-center gap-2">
            <DatePicker
              selected={dateRange.startDate}
              onChange={(date) => handleDateRangeChange('startDate', date)}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
              dateFormat="MMM d, yyyy"
            />
            <span className="text-gray-500">to</span>
            <DatePicker
              selected={dateRange.endDate}
              onChange={(date) => handleDateRangeChange('endDate', date)}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
              dateFormat="MMM d, yyyy"
            />
          </div>
        </div>
      </div>

      {/* Executive Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-3">
            <Users className="text-blue-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Total Teams</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalTeams}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-3">
            <Activity className="text-green-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Active Developers</p>
              <p className="text-2xl font-bold text-gray-900">
                {summary.totalActiveUsers}<span className="text-sm text-gray-500">/{summary.totalUsers}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-3">
            <Code className="text-purple-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Requests/User</p>
              <p className="text-2xl font-bold text-gray-900">{summary.avgRequests}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-3">
            <Target className="text-orange-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Adoption Rate</p>
              <p className="text-2xl font-bold text-gray-900">{summary.overallAdoptionRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performing Team Highlight */}
      {summary.topPerformingTeam && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Award className="text-blue-600" size={24} />
            <h3 className="text-lg font-semibold text-blue-900">Top Performing Team (Per-User)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-blue-600 font-medium">Team Name</p>
              <p className="text-xl font-bold text-blue-900">{summary.topPerformingTeam.teamName}</p>
            </div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Requests/User</p>
              <p className="text-xl font-bold text-blue-900">
                {summary.topPerformingTeam.totalUsers > 0 
                  ? Math.round((summary.topPerformingTeam.totalRequests || 0) / summary.topPerformingTeam.totalUsers)
                  : 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Lines Added/User</p>
              <p className="text-xl font-bold text-blue-900">
                {summary.topPerformingTeam.totalUsers > 0 
                  ? Math.round((summary.topPerformingTeam.totalLinesAdded || 0) / summary.topPerformingTeam.totalUsers).toLocaleString()
                  : 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Acceptance Rate</p>
              <p className="text-xl font-bold text-blue-900">{summary.topPerformingTeam.acceptanceRate}%</p>
            </div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Team Size</p>
              <p className="text-xl font-bold text-blue-900">{summary.topPerformingTeam.totalUsers} users</p>
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Productivity Chart */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Team Productivity Metrics</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#3B82F6]"></div>
                <span className="text-sm text-gray-600">Requests per User</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#10B981]"></div>
                <span className="text-sm text-gray-600">Acceptance %</span>
              </div>
            </div>
          </div>
          {productivityTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={productivityTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}  // Increased height for better spacing
                  interval={0}  // Show all labels
                  tick={{ dy: 10 }}  // Move labels down
                />
                {/* Primary Y-axis for Lines per User */}
                <YAxis 
                  yAxisId="lines"
                  label={{ 
                    value: 'Requests per User', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: 0,
                    dy: 50  // Adjust vertical position
                  }}
                />
                {/* Secondary Y-axis for Percentages (0-100) */}
                <YAxis 
                  yAxisId="percent"
                  orientation="right"
                  domain={[0, 100]}
                  label={{ 
                    value: 'Percentage', 
                    angle: 90, 
                    position: 'insideRight',
                    offset: 0,
                    dy: -50  // Adjust vertical position
                  }}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'Requests per User') return [`${value} requests`, 'Requests per User'];
                    return [`${value}%`, 'Acceptance Rate'];
                  }}
                />
                {/* Legend moved to title area */}
                <Bar yAxisId="lines" dataKey="requestsPerUser" fill="#3B82F6" name="Requests per User" />
                <Bar yAxisId="percent" dataKey="acceptanceRate" fill="#10B981" name="Acceptance %" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">No productivity data available</div>
          )}
        </div>

        {/* Feature Adoption Chart */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">AI Feature Adoption by Team</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#3B82F6]"></div>
                <span className="text-sm text-gray-600">Composer</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#10B981]"></div>
                <span className="text-sm text-gray-600">Chat</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#8B5CF6]"></div>
                <span className="text-sm text-gray-600">Agent</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#F59E0B]"></div>
                <span className="text-sm text-gray-600">Cmd+K</span>
              </div>
            </div>
          </div>
          {featureAdoptionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={featureAdoptionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis 
                  label={{ 
                    value: 'Requests per User', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: 0,
                    dy: 75  // Increased vertical offset to prevent cutoff
                  }} 
                />
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} per user (${props.payload.usersCount} users)`,
                    name.replace(' Requests', '').replace(' Usage', '')
                  ]}
                />
                {/* Legend moved to title area */}
                <Bar dataKey="composerRequests" stackId="a" fill="#3B82F6" name="Composer Requests" />
                <Bar dataKey="chatRequests" stackId="a" fill="#10B981" name="Chat Requests" />
                <Bar dataKey="agentRequests" stackId="a" fill="#8B5CF6" name="Agent Requests" />
                <Bar dataKey="cmdKUsages" stackId="a" fill="#F59E0B" name="Cmd+K Usage" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">No feature adoption data available</div>
          )}
        </div>
      </div>

      {/* Team Performance Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Team Performance Leaderboard</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                Sort by: <strong>Requests/User</strong> ({sortOrder === 'desc' ? 'Descending' : 'Ascending'})
              </span>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setSortBy('teamName');
                    setSortOrder(sortBy === 'teamName' && sortOrder === 'desc' ? 'asc' : 'desc');
                  }}
                >
                  Team Name {sortBy === 'teamName' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setSortBy('totalUsers');
                    setSortOrder(sortBy === 'totalUsers' && sortOrder === 'desc' ? 'asc' : 'desc');
                  }}
                >
                  Team Size {sortBy === 'totalUsers' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setSortBy('activeUsers');
                    setSortOrder(sortBy === 'activeUsers' && sortOrder === 'desc' ? 'asc' : 'desc');
                  }}
                >
                  Active Developers {sortBy === 'activeUsers' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setSortBy('totalLinesAdded');
                    setSortOrder(sortBy === 'totalLinesAdded' && sortOrder === 'desc' ? 'asc' : 'desc');
                  }}
                >
                  Lines Added {sortBy === 'totalLinesAdded' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setSortBy('requestsPerUser');
                    setSortOrder(sortBy === 'requestsPerUser' && sortOrder === 'desc' ? 'asc' : 'desc');
                  }}
                >
                  Requests/User {sortBy === 'requestsPerUser' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setSortBy('acceptanceRate');
                    setSortOrder(sortBy === 'acceptanceRate' && sortOrder === 'desc' ? 'asc' : 'desc');
                  }}
                >
                  Acceptance Rate {sortBy === 'acceptanceRate' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setSortBy('engagementRate');
                    setSortOrder(sortBy === 'engagementRate' && sortOrder === 'desc' ? 'asc' : 'desc');
                  }}
                >
                  Engagement {sortBy === 'engagementRate' && (sortOrder === 'desc' ? '↓' : '↑')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedTeams.length > 0 ? sortedTeams.map((team, index) => (
                <tr key={team.teamId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {index === 0 && <Award className="text-yellow-500 mr-2" size={16} />}
                      <span className="text-sm font-medium text-gray-900">#{index + 1}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {team.teamName || 'Unknown Team'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{team.totalUsers || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{team.activeUsers || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {(team.totalLinesAdded || 0).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {team.totalUsers > 0 ? Math.round((team.totalRequests || 0) / team.totalUsers).toLocaleString() : 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm text-gray-900">{team.acceptanceRate || 0}%</div>
                      {(team.acceptanceRate || 0) >= 80 && (
                        <CheckCircle className="ml-2 text-green-500" size={16} />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      (team.engagementRate || 0) >= 80 ? 'bg-green-100 text-green-800' :
                      (team.engagementRate || 0) >= 50 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {team.engagementRate || 0}%
                    </span>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No team data available for the selected date range
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Teams; 